/**
 * Button component module.
 * @module shared/components/Button
 *
 */
import React from 'react';
import PropTypes from 'prop-types';
import styled, {css} from 'styled-components/macro';
import {_ButtonBase, fill, layoutAttribute, secondaryImportanceButton} from './cssUtils';
import InlineSpinner from './InlineSpinner';
import {hexToRgb} from '../utils/colors';

const getBackgroundColor = (props, opacity) => {
  if (props.importance === 'primary') {
    const rgb = hexToRgb(props.theme.primaryColor);
    return `rgba(${rgb.r},${rgb.g},${rgb.b},${opacity || 1})`;
  }
  if (props.importance === 'secondary') {
    return secondaryImportanceButton(props, opacity)['background-color'];
  }
};

const getFontSize = (props) => {
  if (props.size === 'lg') {
    return '18px';
  }
  if (props.size === 'md') {
    if (props.importance === 'primary') {
      return '16px';
    }
    return secondaryImportanceButton(props)['font-size'];
  }
  if (props.size === 'sm') {
    return '12px';
  }
};

const getColor = (props) => {
  if (props.importance === 'primary') {
    return '#FFFFFF';
  }
  if (props.importance === 'secondary') {
    return secondaryImportanceButton(props).color;
  }
};

const getLabelMargin = (props) => {
  if (props.size === 'lg') {
    return '5px';
  }
  if (['md', 'sm'].includes(props.size)) {
    return '0 5px 0 5px';
  }
};

const _Button = styled.button`
  ${_ButtonBase};
  ${layoutAttribute.css};
  ${props => {
    if (props.theme.neumorphic) {
      return css`
        background-image: ${props.importance === 'primary' ? 'linear-gradient(to bottom right, rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0.1)), linear-gradient(to bottom right, rgba(255, 255, 255, 0.15), rgba(0, 0, 0, 0.15))' : 'linear-gradient(to bottom right, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.1))'};
        box-shadow: inset 1px 1px 1px 0 rgba(255, 255, 255, ${props.importance === 'primary' ? 0.5 : 0.15}),
          inset -1px -1px 1px 0 rgba(0, 0, 0, ${props.importance === 'primary' ? 0.4 : 0.3}),
        2px 2px ${['md', 'lg'].includes(props.size) ? '4px' : '3px'} 0px rgba(0, 0, 0, ${props.importance === 'primary' ? 0.5 : 0.3});
      `;
    }
  }};
  opacity: ${props => props.disabled ? 0.5 : 1};
  cursor: ${props => !props.disabled ? 'pointer' : 'not-allowed'};
  background-color: ${props => getBackgroundColor(props)};
  color: ${props => getColor(props)};
  font-family: ${props => props.theme[props.importance + 'ButtonText'].name};
  font-size: ${props => getFontSize(props)};
  ${(props) => fill(props.width || 'fit-content')};

  &:hover {
    background-color: ${props => getBackgroundColor(props, props.importance === 'primary' ? 0.8 : 0.1)};
  }

  transition: background-color 0.3s;

  ${props => props.size && props.size === 'sm' && `
  padding-top: 0;
  padding-bottom: 0;
  `}
`;

const _Label = styled.span`
  margin: ${props => getLabelMargin(props)};
`;

/**
 * Button component
 * @name Button
 */
const Button = ({importance, size, label, disabled = false, action, form, width, id, processing, layout}) => {
  const [internalProcessing, setInternalProcessing] = React.useState();
  const onClick = e => {
    e.preventDefault();
    e.stopPropagation();
    const prom = action();
    if (prom && prom.then) {
      setInternalProcessing(true);
      prom.finally(() => setInternalProcessing(false));
    }
  };
  return <_Button type={action === 'SUBMIT' ? 'submit' : 'button'}
                  layout={layout}
                  form={form}
                  onClick={!(disabled || internalProcessing || processing) && typeof action === 'function' ? onClick : undefined}
                  width={width}
                  importance={importance}
                  size={size}
                  id={id}
                  disabled={disabled || internalProcessing || processing}>
    {(internalProcessing || processing) ? <InlineSpinner importance={importance} size={size}/>
      : <_Label size={size}>{label}</_Label>}
  </_Button>;
};

export default Button;

Button.propTypes = {
  /**
   * Button level of importance
   */
  importance: PropTypes.oneOf(['primary', 'secondary']).isRequired,
  /**
   * Button size
   */
  size: PropTypes.oneOf(['sm', 'md', 'lg']).isRequired,
  /**
   * Button label text
   */
  label: PropTypes.string.isRequired,
  /**
   * Whether the button is disabled
   */
  disabled: PropTypes.bool,
  /**
   * The width of the button
   */
  width: PropTypes.string,
  /**
   * The action to perform when the button is pressed (and not disabled)
   */
  action: PropTypes.oneOfType([PropTypes.func, PropTypes.string]).isRequired,
  /**
   * The form ID to map to the button
   */
  form: PropTypes.string,
  /**
   * Optional HTML ID
   */
  id: PropTypes.string,
  /**
   * Indicate that the icon is processing
   */
  processing: PropTypes.bool,
  layout: layoutAttribute.propTypes,
};
